import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import GrantAccessModal from './GrantAccessModal';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#3b82f6' },
  { value: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { value: 'enrolled', label: 'Enrolled', color: '#10b981' },
  { value: 'closed', label: 'Closed', color: '#94a3b8' },
];

export default function InquiriesPanel() {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [notesDraft, setNotesDraft] = useState({});
  const [grantInquiry, setGrantInquiry] = useState(null);
  const [unpaidEnrollments, setUnpaidEnrollments] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'registrationInquiries'), orderBy('createdAt', 'desc')),
      (snap) => setInquiries(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('accessStatus', '==', 'pending_payment')),
      (snap) => setUnpaidEnrollments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setUnpaidEnrollments([])
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'unpaid') {
      const q = search.trim().toLowerCase();
      return unpaidEnrollments
        .filter((u) =>
          !q
            ? true
            : [u.displayName, u.firstName, u.lastName, u.email, u.phone, u.city]
                .join(' ')
                .toLowerCase()
                .includes(q)
        )
        .map((u) => ({
          id: u.id,
          fullName: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
          email: u.email,
          phone: u.phone || '—',
          matricYear: '—',
          planLabel: 'Portal signup — payment pending',
          status: 'unpaid',
          createdAt: u.createdAt,
          unpaidAccount: true,
          userId: u.id,
        }));
    }
    let list = filter === 'all' ? inquiries : inquiries.filter((i) => i.status === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.fullName, i.email, i.phone, i.planLabel, i.registrationNumber, i.message]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    return list;
  }, [inquiries, filter, search, unpaidEnrollments]);

  async function updateStatus(id, status) {
    await updateDoc(doc(db, 'registrationInquiries', id), { status });
  }

  async function saveNotes(id) {
    const adminNotes = notesDraft[id] ?? '';
    await updateDoc(doc(db, 'registrationInquiries', id), { adminNotes });
  }

  async function handleGrantSuccess(inquiry, result) {
    await updateDoc(doc(db, 'registrationInquiries', inquiry.id), {
      status: 'enrolled',
      subscriptionGrantedAt: new Date(),
      registrationNumber: result.registrationNumber || '',
      paymentReference: result.payment?.referenceNumber || '',
    });
    setGrantInquiry(null);
  }

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Registration inquiries</h2>
          <p style={s.sub}>
            Students who register on the website and students who enrolled but have not paid appear here.
          </p>
        </div>
        <div style={s.filters}>
          {['all', 'unpaid', ...STATUS_OPTIONS.map((o) => o.value)].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              style={s.filterBtn(filter === f)}
            >
              {f === 'all' ? 'All' : f === 'unpaid' ? 'Unpaid enrollments' : STATUS_OPTIONS.find((o) => o.value === f)?.label}
              {f === 'unpaid' && (
                <span style={s.filterCount}>{unpaidEnrollments.length}</span>
              )}
              {f !== 'all' && f !== 'unpaid' && (
                <span style={s.filterCount}>
                  {inquiries.filter((i) => i.status === f).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search name, email, phone, plan…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.searchInput}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={s.empty}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#cbd5e1' }}>
            inbox
          </span>
          <p>No inquiries match your search</p>
        </div>
      ) : (
        <div style={s.list}>
          {filtered.map((inq) => {
            const statusMeta = inq.unpaidAccount
              ? { label: 'Unpaid enrollment', color: '#c2410c' }
              : STATUS_OPTIONS.find((o) => o.value === inq.status) || STATUS_OPTIONS[0];
            const phoneHref = String(inq.phone || '').replace(/\s/g, '');
            return (
              <div key={inq.id} style={s.card}>
                <div style={s.cardTop}>
                  <div>
                    <h3 style={s.name}>{inq.fullName}</h3>
                    <p style={s.meta}>
                      Matric {inq.matricYear} · {inq.planLabel || 'Plan TBD'} · Submitted {formatDate(inq.createdAt)}
                    </p>
                    {inq.registrationNumber ? (
                      <p style={s.regNo}>Reg No: {inq.registrationNumber}</p>
                    ) : null}
                    {inq.paymentReference ? (
                      <p style={s.regNo}>Payment Ref: {inq.paymentReference}</p>
                    ) : null}
                  </div>
                  <span style={s.badge(statusMeta.color)}>{statusMeta.label}</span>
                </div>

                <div style={s.contactRow}>
                  <a href={`mailto:${inq.email}`} style={s.contactLink}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mail</span>
                    {inq.email}
                  </a>
                  {phoneHref && phoneHref !== '—' ? (
                    <a href={`tel:${phoneHref}`} style={s.contactLink}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>call</span>
                      {inq.phone}
                    </a>
                  ) : null}
                </div>

                {inq.message ? <p style={s.message}>{inq.message}</p> : null}

                {inq.unpaidAccount ? (
                  <p style={s.message}>
                    This student enrolled on the website and has not completed card or cash payment yet.
                    Open Subscriptions → View full profile, or record a payment to activate access.
                  </p>
                ) : (
                <>
                <div style={s.statusRow}>
                  <label style={s.statusLabel}>
                    Status
                    <select
                      value={inq.status || 'new'}
                      onChange={(e) => updateStatus(inq.id, e.target.value)}
                      style={s.select}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => setGrantInquiry(inq)}
                    style={s.grantBtn(false)}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>card_membership</span>
                    Grant access
                  </button>
                </div>

                <div style={s.notesRow}>
                  <input
                    type="text"
                    placeholder="Admin notes (contact method, follow-up…)"
                    value={notesDraft[inq.id] ?? inq.adminNotes ?? ''}
                    onChange={(e) => setNotesDraft((p) => ({ ...p, [inq.id]: e.target.value }))}
                    style={s.notesInput}
                  />
                  <button type="button" onClick={() => saveNotes(inq.id)} style={s.saveNotesBtn}>
                    Save
                  </button>
                </div>
                </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {grantInquiry ? (
        <GrantAccessModal
          inquiry={grantInquiry}
          onClose={() => setGrantInquiry(null)}
          onSuccess={(result) => handleGrantSuccess(grantInquiry, result)}
        />
      ) : null}
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { marginBottom: 24 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#4b00d1', marginBottom: 6 },
  sub: { fontSize: 13, color: '#64748b', maxWidth: 720, lineHeight: 1.6, marginBottom: 16 },
  filters: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  searchInput: {
    width: '100%',
    maxWidth: 400,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
  },
  filterBtn: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 999,
    border: active ? '1.5px solid #0f172a' : '1.5px solid #e2e8f0',
    background: active ? '#4b00d1' : '#fff',
    color: active ? '#fff' : '#64748b',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  }),
  filterCount: {
    background: 'rgba(255,255,255,0.2)',
    padding: '1px 7px',
    borderRadius: 999,
    fontSize: 11,
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#94a3b8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 14 },
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    padding: '18px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  name: { fontSize: 16, fontWeight: 700, color: '#4b00d1', marginBottom: 4 },
  meta: { fontSize: 12, color: '#94a3b8' },
  regNo: { fontSize: 12, color: '#059669', fontWeight: 700, marginTop: 4, fontFamily: 'monospace' },
  badge: (color) => ({
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 999,
    background: `${color}18`,
    color,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  }),
  contactRow: { display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 },
  contactLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: '#1e40af',
    textDecoration: 'none',
    fontWeight: 500,
  },
  message: { fontSize: 13, color: '#475569', marginBottom: 12, fontStyle: 'italic' },
  statusRow: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 },
  statusLabel: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11, fontWeight: 600, color: '#64748b' },
  select: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    minWidth: 140,
  },
  grantBtn: (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 16px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#94a3b8' : '#059669',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  notesRow: { display: 'flex', gap: 8 },
  notesInput: {
    flex: 1,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
  },
  saveNotesBtn: {
    padding: '9px 14px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#475569',
  },
};
